import { buildWorkInputAttemptContext } from './work-attempt-policy.mjs'
import { isWithdrawnWorkResult } from '../../policies/work-result-status-policy.mjs'
import {
  getLatestWorkFormInputVersion,
  normalizeWorkFormInput,
} from './work-form-input-policy.mjs'

function positiveInteger(value, label) {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new TypeError(`${label} ID를 확인해 주세요.`)
  }
  return parsed
}

function responseData(error = {}) {
  if (error?.response?.data) return error.response.data
  if (error?.error && typeof error.error === 'object') return error.error
  return error || {}
}

export function normalizeIncomingInputFiles(incoming = []) {
  if (!Array.isArray(incoming)) return []
  return incoming.flatMap(source => {
    const versions = Array.isArray(source?.input_versions) ? source.input_versions : []
    return versions
      .filter(version => version?.file_id || version?.availability === 'restricted'
        || (source.item_type === 'file_input' && version?.metadata?.use_default === true))
      .map(version => ({
        key: `${source.id || source.key || source.client_key}-${version.id || version.version_no || version.file_id || version.display_name}`,
        source_key: source.key || source.client_key || source.id,
        source_name: source.name || '선행 할일',
        source_status: source.status,
        version,
        file_name: version.availability === 'restricted'
          ? '접근 제한'
          : version.metadata?.use_default === true
            ? '입력할 파일 없음'
            : version.original_filename || version.display_name || version.name || `v${version.version_no || '-'}`,
      }))
  })
}

export function getIncomingFormInputSources(incoming = []) {
  if (!Array.isArray(incoming)) return []
  const seen = new Set()
  return incoming.filter(source => {
    const id = source?.id || source?.work_item_id
    if (source?.item_type !== 'form_input' || !id || seen.has(String(id))) return false
    seen.add(String(id))
    return true
  })
}

export function normalizeIncomingFormInput(source = {}, detail = {}) {
  const version = getLatestWorkFormInputVersion(
    detail?.input_versions,
    detail?.current_attempt_id,
  )
  if (!version) return null
  const sourceId = source.id || source.work_item_id
  return {
    key: `incoming-form-${sourceId}-${version.id || version.version_no}`,
    source_id: sourceId,
    source_key: source.key || source.client_key || sourceId,
    source_name: source.name || detail.name || '선행 폼 입력',
    source_status: source.status || detail.status,
    version,
    value: normalizeWorkFormInput(version.metadata),
  }
}

export async function loadIncomingFormInputDetails(incoming = [], getWorkItem) {
  const sources = getIncomingFormInputSources(incoming)
  const results = await Promise.allSettled(
    sources.map(source => getWorkItem(source.id || source.work_item_id)),
  )
  const inputs = []
  const errors = []
  results.forEach((result, index) => {
    const source = sources[index]
    if (result.status === 'rejected') {
      errors.push({ source, error: result.reason })
      return
    }
    const input = normalizeIncomingFormInput(source, result.value)
    if (input) inputs.push(input)
  })
  return { inputs, errors }
}

export function buildWorkFileInputPayload({ item = {}, file = {}, useDefault = false, allowInitialMainAttempt = false } = {}) {
  if (useDefault === true) {
    return {
      ...buildWorkInputAttemptContext(item, { allowInitialMainAttempt }),
      input_type: 'data',
      display_name: '입력할 파일 없음',
      metadata: { use_default: true },
    }
  }
  return {
    ...buildWorkInputAttemptContext(item, { allowInitialMainAttempt }),
    input_type: 'data',
    file_id: positiveInteger(file.id ?? file.file_id, 'MTD'),
    display_name: file.name || file.original_filename || file.display_name || `MTD #${file.id ?? file.file_id}`,
    metadata: {
      source: 'folder_list',
      category: file.category ?? null,
    },
  }
}

export const isFailedWorkRun = run => run?.status === 'failed'

export const isReviewableWorkRun = run => run?.status === 'reviewing' && !isWithdrawnWorkResult(run)

export function workRunDisplayOutputs(run, outputs = []) {
  return isFailedWorkRun(run) ? [] : outputs
}

export function workRunFailureMessage(run = {}) {
  const code = run?.run_result?.error_code || run?.error_code || ''
  const message = FIRST_DEMO_ERROR_MESSAGES[code]
    || run?.run_result?.message
    || 'RPA 실행에 실패했습니다.'
  return code ? `[${code}] ${message}` : message
}

export function workRunFailure(run = {}) {
  return {
    error: true,
    code: run?.run_result?.error_code || run?.error_code || 'RPA_ENGINE_RUN_FAILED',
    message: workRunFailureMessage(run),
    run,
  }
}

function normalizeExplicitReviewTarget(target = {}) {
  const type = target.type || 'rpa'
  const normalized = {
    ...target,
    type,
  }
  if (type === 'mtd') {
    normalized.run_id = target.run_id ?? null
    normalized.output_id = target.output_id ?? null
    normalized.file_id = target.file_id ?? target.id ?? null
  }
  return normalized
}

function normalizeLegacyReviewOutput(output = {}, item = {}) {
  return {
    ...output,
    type: 'rpa',
    run_id: output.run_id ?? item.review_target_run_id ?? item.metadata?.review_target_run_id ?? null,
    output_id: output.output_id ?? output.id ?? null,
  }
}

export function normalizeWorkReviewTargets(item = {}) {
  const explicitTargets = item.review_targets || item.metadata?.review_targets || []
  if (Array.isArray(explicitTargets) && explicitTargets.length) {
    return explicitTargets.map(normalizeExplicitReviewTarget)
  }

  const legacyOutputs = item.review_target_outputs || item.metadata?.review_target_outputs || []
  if (!Array.isArray(legacyOutputs)) return []
  return legacyOutputs.map(output => normalizeLegacyReviewOutput(output, item))
}

const FIRST_DEMO_ERROR_MESSAGES = {
  WORK_FORM_INPUT_REQUIRED: '현재 회차의 폼 입력을 등록한 뒤 완료해 주세요.',
  WORK_INPUT_DEFAULT_FLAG_INVALID: '입력 없음 설정 형식이 올바르지 않습니다. 새로고침 후 다시 선택해 주세요.',
  WORK_INPUT_DEFAULT_CONFLICT: '입력 없음과 실제 파일 또는 행을 함께 등록할 수 없습니다. 입력 상태를 확인해 주세요.',
  RPA_INPUT_DEFAULT_CONFLICT: '선행 입력에 입력 없음과 실제 파일이 함께 저장되어 있습니다. 선행 입력을 다시 등록해 주세요.',
  RPA_INPUT_DEFAULT_EXAMPLE_INVALID: '입력할 파일이 없을 때 사용할 워크플로우 예시 데이터와 열 정의를 확인해 주세요.',
  WORK_INPUT_TYPE_MISMATCH: '할일 유형과 입력 유형이 일치하지 않습니다.',
  RPA_INPUT_MAPPING_REQUIRED: '필수 워크플로우 입력 매핑이 없습니다. Runtime Branch 템플릿에서 직접 선행 입력을 연결한 뒤 새 업무를 발행해 주세요. 기존 업무의 동결 설정에는 소급 반영되지 않습니다.',
  RPA_INPUT_SOURCE_NOT_FOUND: '매핑 원본 할일을 찾을 수 없습니다. 입력 연결을 다시 선택해 주세요.',
  RPA_INPUT_SOURCE_BRANCH_MISMATCH: '매핑된 선행 할일이 현재 Branch에 속하지 않습니다. 입력 연결을 다시 선택해 주세요.',
  RPA_INPUT_SOURCE_NOT_DIRECT_PREDECESSOR: '직접 선행하는 할일만 워크플로우 입력으로 선택할 수 있습니다.',
  RPA_INPUT_SOURCE_ATTEMPT_NOT_FOUND: '현재 Branch 회차의 선행 할일 실행 정보를 찾을 수 없습니다. 새로고침 후 다시 시도해 주세요.',
  RPA_INPUT_SOURCE_ATTEMPT_NOT_COMPLETED: '현재 Branch 회차의 선행 할일을 완료한 뒤 다시 실행해 주세요.',
  RPA_INPUT_SOURCE_NOT_COMPLETED: '선행 할일을 완료한 뒤 다시 실행해 주세요.',
  RPA_INPUT_VERSION_NOT_FOUND: '선행 입력을 저장한 뒤 다시 실행해 주세요.',
  RPA_INPUT_TYPE_MISMATCH: 'Form/File 입력 유형과 워크플로우 매핑이 일치하지 않습니다.',
  RPA_INPUT_FORM_STRUCTURE_INVALID: '저장된 Form 구조를 확인한 뒤 다시 실행해 주세요.',
  RPA_INPUT_FILE_NOT_FOUND: '워크플로우에 사용할 파일을 선택하고 등록해 주세요.',
  RPA_ENGINE_RUN_FAILED: 'RPA 실행에 실패했습니다. 실행 이력을 확인한 뒤 다시 실행해 주세요.',
  RPA_ENGINE_OUTPUT_NOT_FOUND: 'RPA 결과 파일이 생성되지 않았습니다. 실행 이력을 확인한 뒤 다시 실행해 주세요.',
  WORK_REVIEW_TARGET_NOT_READY: '검토 대상이 아직 준비되지 않았습니다. 잠시 후 다시 불러와 주세요.',
  TREE_VERSION_CONFLICT: 'Work tree가 변경되었습니다. 최신 내용을 다시 불러온 뒤 다시 시도해 주세요.',
}

export function workManagementFirstDemoErrorMessage(error, code) {
  const data = responseData(error)
  const detail = data.error && typeof data.error === 'object' ? data.error : data
  if (code === 'RPA_ENGINE_RUN_FAILED' && data.message) return data.message
  if (code === 'RPA_INPUT_DEFAULT_EXAMPLE_INVALID') {
    const reasons = {
      example_data_missing: '워크플로우에 예시 데이터가 없습니다.',
      example_tables_empty: '워크플로우 예시 데이터에 테이블이 없습니다.',
      example_table_header_empty: '워크플로우 예시 테이블에 열 정의가 없습니다.',
    }
    const activity = detail.activity_id != null ? `입력 단계 ${detail.activity_id}: ` : ''
    return `${activity}${reasons[detail.reason] || '워크플로우 예시 구조가 올바르지 않습니다.'} 워크플로우의 예시 데이터를 설정한 뒤 다시 실행해 주세요.`
  }
  if (['WORK_FORM_STRUCTURE_INVALID', 'WORK_INPUT_DEFAULT_FLAG_INVALID', 'WORK_INPUT_DEFAULT_CONFLICT'].includes(code)) {
    const details = detail.details || data.details || detail
    const location = [details.path, details.reason].filter(Boolean).join(': ')
    const message = FIRST_DEMO_ERROR_MESSAGES[code] || '폼 구조를 확인해 주세요.'
    return `${message}${location ? ` ${location}` : ''}`
  }
  return FIRST_DEMO_ERROR_MESSAGES[code] || ''
}
